import type { Metric } from 'web-vitals';

// Define our own custom metric type for component load times
interface CustomMetric {
    name: string;
    value: number;
    delta?: number;
    id: string;
    // Add any other properties needed
}

/**
 * Sends web vitals metrics to Google Analytics or other analytics providers
 * 
 * @param metric - The web vital metric to report
 */
export const sendToAnalytics = (metric: Metric | CustomMetric): void => {
    // Get the existing dataLayer or create a new one
    const dataLayer = (window as Window & { dataLayer?: unknown[] }).dataLayer || [];

    // Add data to dataLayer
    dataLayer.push({
        event: 'web-vitals',
        event_category: 'Web Vitals',
        event_action: metric.name,
        event_value: Math.round(metric.value),
        event_label: metric.id,
        non_interaction: true,
    });

    // Log metrics in development
    if (process.env.NODE_ENV === 'development') {
        console.log(`Web Vitals: ${metric.name}`, metric);
    }
};

/**
 * Reports web vitals metrics
 * 
 * @param onPerfEntry - Callback function to handle the metrics
 */
export const reportWebVitals = async (onPerfEntry?: (metric: Metric) => void): Promise<void> => {
    if (onPerfEntry && typeof onPerfEntry === 'function') {
        try {
            // Import web-vitals library dynamically (using v5.x API)
            const { onCLS, onFCP, onLCP, onTTFB, onINP } = await import('web-vitals');

            // Register metrics
            onCLS(onPerfEntry);     // Cumulative Layout Shift
            onFCP(onPerfEntry);     // First Contentful Paint
            onLCP(onPerfEntry);     // Largest Contentful Paint
            onTTFB(onPerfEntry);    // Time to First Byte
            onINP(onPerfEntry);     // Interaction to Next Paint
        } catch (error) {
            console.error('Failed to load web-vitals library:', error);
        }
    }
};

/**
 * Custom hook to measure and report Component Load Time
 * Can be used to measure any specific component's render performance
 * 
 * @param componentName - Name of the component being measured
 */
export const measureComponentLoad = (componentName: string): () => void => {
    const startTime = performance.now();

    return () => {
        const endTime = performance.now();
        const loadTime = endTime - startTime;

        // Create a custom metric for component load time
        const customMetric: CustomMetric = {
            name: 'COMP_LOAD',
            id: componentName,
            value: loadTime,
            delta: loadTime,
        };

        sendToAnalytics(customMetric);

        if (process.env.NODE_ENV === 'development') {
            console.log(`Component Load Time (${componentName}): ${loadTime.toFixed(2)}ms`);
        }
    };
};

export default reportWebVitals; 